import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import StatsOverview from './components/StatsOverview';
import DomainCard from './components/DomainCard';
import DomainTable from './components/DomainTable';
import DomainModal from './components/DomainModal';
import DomainDetailModal from './components/DomainDetailModal';
import NotificationDrawer from './components/NotificationDrawer';
import Pagination from './components/Pagination';

import { Search, Filter, LayoutGrid, List, Plus, RefreshCw, CheckCircle2, ShieldAlert, Sparkles } from 'lucide-react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { apiFetch } from './api';
import BillingPage from './pages/BillingPage';
import ProformaPage from './pages/ProformaPage';
import StickyNotesModal from './components/StickyNotesModal';

export default function App() {
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);   
  const [loading, setLoading] = useState(true);

  // Filters & Views
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [registrarFilter, setRegistrarFilter] = useState('all');
  const [hostingFilter, setHostingFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'grid' | 'table'
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals & Drawers
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [editingDomain, setEditingDomain] = useState(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  // Toast Banner
  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, registrarFilter, hostingFilter]);

  // Fetch Stats & Notifications
  const fetchStatsAndNotifications = useCallback(async () => {
    if (!localStorage.getItem('authToken')) return;
    try {
    const [statsRes, notifRes] = await Promise.all([
  apiFetch('/stats'),
  apiFetch('/notifications')
]);
      const statsData = await statsRes.json();
      const notifData = await notifRes.json();
      if (statsData.success) setStats(statsData.stats);
      if (notifData.success) setNotifications(notifData.notifications || []);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Fetch Domains List
const fetchDomains = useCallback(async () => {
  if (!localStorage.getItem('authToken')) return;
  setLoading(true);

  try {
    let url = '/domains?';

    if (searchQuery) {
      url += `q=${encodeURIComponent(searchQuery)}&`;
    }

    if (statusFilter && statusFilter !== 'all') {
      url += `status=${statusFilter}&`;
    }

    if (registrarFilter && registrarFilter !== 'all') {
      url += `registrar=${encodeURIComponent(registrarFilter)}&`;
    }

    if (hostingFilter && hostingFilter !== 'all') {
      url += `hosting=${encodeURIComponent(hostingFilter)}&`;
    }

    const res = await apiFetch(url);
    const data = await res.json();

    if (data.success) {
      setDomains(data.domains || []);
    }
  } catch (err) {
    console.error('Error fetching domains:', err);

    if (!err.message.includes('Session expired')) {
      showToast(
        'Failed to connect to backend server.',
        'error'
      );
    }
  } finally {
    setLoading(false);
  }
}, [
  searchQuery,
  statusFilter,
  registrarFilter,
  hostingFilter
]);
  useEffect(() => {
    fetchDomains();
    fetchStatsAndNotifications();
  }, [fetchDomains, fetchStatsAndNotifications]);
const handleLogout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');

  navigate('/login', { replace: true });
};
  // Calculate Paginated Domains
  const totalPages = Math.ceil(domains.length / itemsPerPage);
  const displayedDomains = domains.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Handle Save (Add or Update)
const handleSaveDomain = async (formData) => {
  try {
    const endpoint = editingDomain
      ? `/domains/${editingDomain.id}`
      : '/domains';

    const method = editingDomain ? 'PUT' : 'POST';

    const res = await apiFetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const data = await res.json();

    if (data.success) {
      showToast(
        data.message || 'Domain saved successfully!'
      );

      setIsAddModalOpen(false);
      setEditingDomain(null);

      fetchDomains();
      fetchStatsAndNotifications();
    } else {
      alert(data.message || 'Error saving domain');
    }
  } catch (err) {
    alert(
      'Failed to save domain details: ' + err.message
    );
  }
};
  // Handle Quick Renew
const handleRenewDomain = async (id) => {
  try {
    const res = await apiFetch(`/domains/${id}/renew`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        years: 1,
      }),
    });

    const data = await res.json();

    if (data.success) {
      showToast(data.message, 'success');

      if (
        selectedDomain &&
        selectedDomain.id === id
      ) {
        setSelectedDomain(data.domain);
      }

      fetchDomains();
      fetchStatsAndNotifications();
    } else {
      alert(data.message || 'Error renewing domain');
    }
  } catch (err) {
    alert(
      'Failed to renew domain: ' + err.message
    );
  }
};

  // Handle Delete
const handleDeleteDomain = async (id) => {
  if (
    !window.confirm(
      'Are you sure you want to delete this domain record?'
    )
  ) {
    return;
  }

  try {
    const res = await apiFetch(`/domains/${id}`, {
      method: 'DELETE',
    });

    const data = await res.json();

    if (data.success) {
      showToast(
        'Domain record deleted successfully'
      );

      if (
        selectedDomain &&
        selectedDomain.id === id
      ) {
        setSelectedDomain(null);
      }

      fetchDomains();
      fetchStatsAndNotifications();
    } else {
      alert(
        data.message || 'Error deleting domain'
      );
    }
  } catch (err) {
    alert(
      'Error deleting domain: ' + err.message
    );
  }
};

  const handleToggleAmc = async (domain) => {
    try {
      const currentYear = new Date().getFullYear();
      const isPaid = (domain.amcPaidYear || 0) >= currentYear;
      const newAmcPaidYear = isPaid ? currentYear - 1 : currentYear;
      
      const res = await apiFetch(`/domains/${domain.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...domain, amcPaidYear: newAmcPaidYear }),
      });
      
      const data = await res.json();
      if (data.success) {
        showToast(`AMC marked as ${!isPaid ? 'Paid' : 'Pending'} for ${domain.domainName}`);
        fetchDomains();
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to update AMC status', 'error');
    }
  };

  // Unique Lists for Filter Dropdowns
  const uniqueRegistrars = Array.from(new Set(domains.map(d => d.registrar).filter(Boolean)));
  const uniqueHosting = Array.from(new Set(domains.map(d => d.hostingProvider).filter(Boolean)));

  // Dashboard JSX
  const dashboard = (
    <div className="app-container">
      <Header
  notificationCount={notifications.length}
  onOpenNotifications={() => setIsNotificationOpen(true)}
  onAddDomain={() => {
    setEditingDomain(null);
    setIsAddModalOpen(true);
  }}
  onGenerateInvoice={() => navigate('/billing')}
  onGenerateProforma={() => navigate('/proforma')}
  onLogout={handleLogout}
  activeFilter={statusFilter}
  onFilterChange={setStatusFilter}
  onnotesApp={() => setIsNotesOpen(true)}
/>
      <StatsOverview stats={stats} activeFilter={statusFilter} onFilterChange={setStatusFilter} />
      <div className="glass-panel filter-bar-container" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.08)' }}>
        <div className="filter-bar-search">
          <div style={{ position: 'relative', width: '100%' }}>
            <Search size={19} color="#6366f1" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Search domains, platforms, hosting, notes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="form-control" style={{ paddingLeft: '44px', borderRadius: '14px', background: '#f8fafc', borderColor: '#cbd5e1' }} />
          </div>
        </div>
        <div className="filter-bar-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Filter size={15} color="#4338ca" />
            <select value={registrarFilter} onChange={e => setRegistrarFilter(e.target.value)} className="form-control" style={{ padding: '9px 14px', width: 'auto', fontSize: '0.86rem', borderRadius: '12px', background: '#eef2ff', borderColor: '#c7d2fe', fontWeight: '600', color: '#3730a3' }}>
              <option value="all">All Registrars</option>
              {uniqueRegistrars.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select value={hostingFilter} onChange={e => setHostingFilter(e.target.value)} className="form-control" style={{ padding: '9px 14px', width: 'auto', fontSize: '0.86rem', borderRadius: '12px', background: '#ecfeff', borderColor: '#cff4fc', fontWeight: '600', color: '#0891b2' }}>
              <option value="all">All Hosting</option>
              {uniqueHosting.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '4px' }}>
            <button onClick={() => setViewMode('grid')} style={{ background: viewMode === 'grid' ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'transparent', color: viewMode === 'grid' ? '#ffffff' : '#64748b', border: 'none', padding: '7px 12px', borderRadius: '9px', cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none' }} title="Grid View">
              <LayoutGrid size={17} />
            </button>
            <button onClick={() => setViewMode('table')} style={{ background: viewMode === 'table' ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'transparent', color: viewMode === 'table' ? '#ffffff' : '#64748b', border: 'none', padding: '7px 12px', borderRadius: '9px', cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: viewMode === 'table' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none' }} title="Table View">
              <List size={17} />
            </button>
          </div>
        </div>
      </div>
      {domains.length === 0 && !loading ? (
        <div className="glass-panel" style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-muted)', background: '#ffffff', borderRadius: '24px' }}>
          <Sparkles size={52} color="#6366f1" style={{ marginBottom: '16px', opacity: 0.9 }} />
          <h3 style={{ fontSize: '1.4rem', color: '#0f172a', marginBottom: '8px', fontWeight: '800' }}>No Domains Found</h3>
          <p style={{ fontSize: '0.92rem', marginBottom: '22px' }}>{searchQuery || statusFilter !== 'all' ? 'Try adjusting your search query or filters.' : 'Get started by adding your first domain detail!'}</p>
          <button className="btn btn-primary" onClick={() => { setEditingDomain(null); setIsAddModalOpen(true); }}>
            <Plus size={18} /> Add New Domain
          </button>
          <button className="btn btn-secondary ml-2" onClick={() => navigate('/billing')}>
            <Plus size={18} /> Generate Invoice
          </button>
          <button className="btn btn-secondary" style={{ marginLeft: '8px' }} onClick={() => navigate('/proforma')}>
            <Plus size={18} /> Generate Proforma
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="domains-grid">
              {displayedDomains.map(domain => (
                <DomainCard key={domain.id} domain={domain} onSelect={setSelectedDomain} onEdit={dom => { setEditingDomain(dom); setIsAddModalOpen(true); }} onDelete={handleDeleteDomain} onRenew={handleRenewDomain} />
              ))}
            </div>
          ) : (
            <DomainTable loading={loading} domains={displayedDomains} onSelect={setSelectedDomain} onEdit={dom => { setEditingDomain(dom); setIsAddModalOpen(true); }} onDelete={handleDeleteDomain} onRenew={handleRenewDomain} onToggleAmc={handleToggleAmc} />
          )}
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={domains.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
        </>
      )}
      <DomainModal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); setEditingDomain(null); }} onSave={handleSaveDomain} domain={editingDomain} />
      <DomainDetailModal isOpen={Boolean(selectedDomain)} onClose={() => setSelectedDomain(null)} domain={selectedDomain} onRenew={handleRenewDomain} onEdit={dom => { setSelectedDomain(null); setEditingDomain(dom); setIsAddModalOpen(true); }} />
      <NotificationDrawer isOpen={isNotificationOpen} onClose={() => setIsNotificationOpen(false)} notifications={notifications} onRenew={handleRenewDomain} onSelectDomain={setSelectedDomain} />
      <StickyNotesModal open={isNotesOpen} onClose={() => setIsNotesOpen(false)} />
      {toast && (
        <div className="toast-banner">
          {toast.type === 'error' ? <ShieldAlert size={22} color="#dc2626" /> : <CheckCircle2 size={22} color="#059669" />}
          <span style={{ fontSize: '0.92rem', fontWeight: '700' }}>{toast.message}</span>
        </div>
      )}
    </div>
  );

 return (
  <Routes>

    {/* Public Login */}
    <Route
      path="/login"
      element={<LoginPage />}
    />

    {/* Protected Dashboard */}
    <Route
      path="/"
      element={
        <ProtectedRoute>
          {dashboard}
        </ProtectedRoute>
      }
    />

    {/* Protected Billing */}
    <Route
      path="/billing"
      element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      }
    />

    {/* Protected Proforma */}
    <Route
      path="/proforma"
      element={
        <ProtectedRoute>
          <ProformaPage />
        </ProtectedRoute>
      }
    />

  </Routes>
);
}
